import { useState, useRef, useEffect } from "react";
import DashboardLayout from "../components/dashboard-layout";
import api from "../api";

const QUICK_CHIPS = [
  { icon: "⚡", label: "LED blink Arduino" },
  { icon: "🌡️", label: "Sensor DHT22" },
  { icon: "📶", label: "ESP32 WiFi" },
  { icon: "⚙️", label: "Servo motor" },
  { icon: "👁️", label: "Sensor ultrasonic" },
  { icon: "💾", label: "I2C LCD 16x2" },
];

/* Sama persis dengan mobile */
const BLOCK_TYPES = [
  { tag: "PENJELASAN", icon: "📖", color: "#059669", bg: "rgba(16,185,129,0.08)", title: "Penjelasan",    textColor: "#065F46", border: "#059669" },
  { tag: "INSTRUKSI",  icon: "⚡", color: "#2563EB", bg: "rgba(59,130,246,0.08)",  title: "Instruksi",    textColor: "#1E3A8A", border: "#2563EB" },
  { tag: "RANGKAIAN",  icon: "🔌", color: "#0891B2", bg: "rgba(8,145,178,0.08)",   title: "Cara Merangkai",textColor: "#164E63", border: "#0891B2" },
  { tag: "TIPS",       icon: "💡", color: "#D97706", bg: "rgba(245,158,11,0.08)",  title: "Tips",         textColor: "#78350F", border: "#D97706" },
  { tag: "APLIKASI",   icon: "🚀", color: "#7C3AED", bg: "rgba(139,92,246,0.08)", title: "Aplikasi",     textColor: "#4C1D95", border: "#7C3AED" },
];

/* ── Parser sama dengan mobile ── */
function parseContent(text) {
  const parts = [];
  // Hapus blok kode dulu agar tag di dalam kode tidak ikut terparsing
  const codeRegex = /```(\w+)?\n?([\s\S]*?)```/g;
  const codePlaceholders = [];
  const sanitized = text.replace(codeRegex, (match, lang, code) => {
    const idx = codePlaceholders.length;
    codePlaceholders.push({ lang: lang || "cpp", content: code.trim() });
    return `__CODE_BLOCK_${idx}__`;
  });

  const blockRegex = /\[(PENJELASAN|INSTRUKSI|RANGKAIAN|TIPS|APLIKASI)\]([\s\S]*?)\[\/\1\]/gi;
  const segments = [];
  let lastIdx = 0, bm;
  while ((bm = blockRegex.exec(sanitized)) !== null) {
    if (bm.index > lastIdx) segments.push({ type: "raw", content: sanitized.slice(lastIdx, bm.index) });
    segments.push({ type: "block", tag: bm[1].toUpperCase(), content: bm[2].trim() });
    lastIdx = bm.index + bm[0].length;
  }
  if (lastIdx < sanitized.length) segments.push({ type: "raw", content: sanitized.slice(lastIdx) });

  const placeholderRegex = /__CODE_BLOCK_(\d+)__/g;

  segments.forEach(seg => {
    const content = seg.type === "block" ? seg.content : seg.content;
    // Cek apakah ada placeholder kode di dalam segmen ini
    const hasCode = placeholderRegex.test(content);
    placeholderRegex.lastIndex = 0;

    if (seg.type === "block" && !hasCode) {
      parts.push({ type: "info", tag: seg.tag, content: seg.content });
    } else if (seg.type === "block" && hasCode) {
      // Pisahkan konten info dari kode yang ada di dalamnya
      const subParts = content.split(placeholderRegex);
      let cleanInfoContent = "";
      for (let i = 0; i < subParts.length; i++) {
        if (i % 2 === 0) {
          // teks info biasa
          cleanInfoContent += subParts[i];
        } else {
          // placeholder index
          if (cleanInfoContent.trim()) {
            parts.push({ type: "info", tag: seg.tag, content: cleanInfoContent.trim() });
            cleanInfoContent = "";
          }
          const codeIdx = parseInt(subParts[i], 10);
          parts.push({ type: "code", ...codePlaceholders[codeIdx] });
        }
      }
      if (cleanInfoContent.trim()) {
        parts.push({ type: "info", tag: seg.tag, content: cleanInfoContent.trim() });
      }
    } else {
      // raw segment — pisahkan teks dan kode
      const subParts = content.split(placeholderRegex);
      for (let i = 0; i < subParts.length; i++) {
        if (i % 2 === 0) {
          const t = subParts[i].trim();
          if (t) parts.push({ type: "text", content: t });
        } else {
          const codeIdx = parseInt(subParts[i], 10);
          parts.push({ type: "code", ...codePlaceholders[codeIdx] });
        }
      }
    }
  });

  return parts;
}

/* ── Render markdown inline (bold, italic, `code`) ── */
function renderMarkdownText(text, colorOverride) {
  if (!text) return null;
  const tokenRegex = /\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`/g;
  const result = [];
  let lastIndex = 0, match, key = 0;

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(<span key={key++} style={{ color: colorOverride }}>{text.slice(lastIndex, match.index)}</span>);
    }
    if (match[1] !== undefined) {
      result.push(<strong key={key++} style={{ color: colorOverride }}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      result.push(<em key={key++} style={{ color: colorOverride }}>{match[2]}</em>);
    } else if (match[3] !== undefined) {
      result.push(
        <code key={key++} className="chat-inline-code" style={{ background: "rgba(15,158,168,0.10)", color: "#0F9EA8" }}>
          {match[3]}
        </code>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    result.push(<span key={key++} style={{ color: colorOverride }}>{text.slice(lastIndex)}</span>);
  }
  return result;
}

/* ── Code Block — persis seperti mobile ── */
function CodeBlock({ lang, content }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="cb-wrapper">
      <div className="cb-header">
        <div className="cb-dots">
          <span style={{ background: "#ff5f57" }} />
          <span style={{ background: "#febc2e" }} />
          <span style={{ background: "#28c840" }} />
        </div>
        <span className="cb-lang">{lang.toUpperCase()}</span>
        <button className="cb-copy" onClick={copy}>
          <span>{copied ? "✓" : "📋"}</span>
          <span style={{ color: copied ? "#28c840" : "rgba(255,255,255,0.6)" }}>
            {copied ? "Disalin!" : "Salin"}
          </span>
        </button>
      </div>
      <div className="cb-scroll">
        <pre className="cb-pre"><code>{content}</code></pre>
      </div>
    </div>
  );
}

/* ── Info Block — persis seperti mobile ── */
function InfoBlock({ tag, content }) {
  const def = BLOCK_TYPES.find(b => b.tag === tag) || BLOCK_TYPES[0];
  const lines = content.split("\n").filter(l => l.trim());

  return (
    <div className="ib-card" style={{ background: def.bg, borderLeftColor: def.border }}>
      {/* Header */}
      <div className="ib-header">
        <div className="ib-icon-wrap" style={{ background: def.color + "22" }}>
          <span>{def.icon}</span>
        </div>
        <span className="ib-title" style={{ color: def.color }}>{def.title}</span>
      </div>
      {/* Body */}
      <div className="ib-body">
        {lines.map((line, i) => {
          const trimmed = line.trim();
          const isCategory = /^[\u{1F393}\u{1F3ED}\u{1F3E0}⚡💡🔬🔧🎓🏭🏠]/u.test(trimmed);
          const isNumbered = /^(\d+\.|[A-Z]\.)/.test(trimmed);
          const lineContent = isNumbered ? trimmed.replace(/^(\d+\.|[A-Z]\.)\s*/, "") : trimmed;
          const bulletNum = trimmed.match(/^(\d+|[A-Z])/)?.[0];

          return (
            <div key={i} className={`ib-line-row ${isCategory ? "category" : ""}`}>
              {isNumbered && (
                <div className="ib-bullet" style={{ background: def.color }}>
                  <span className="ib-bullet-num">{bulletNum}</span>
                </div>
              )}
              <p className="ib-line-text" style={{
                color: isCategory ? def.color : def.textColor,
                fontWeight: isCategory ? "700" : "400",
              }}>
                {renderMarkdownText(lineContent, isCategory ? def.color : def.textColor)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Message Content Renderer ── */
function MessageContent({ content }) {
  const parts = parseContent(content);
  return (
    <div className="msg-content">
      {parts.map((part, i) => {
        if (part.type === "info") return <InfoBlock key={i} tag={part.tag} content={part.content} />;
        if (part.type === "code") return <CodeBlock key={i} lang={part.lang} content={part.content} />;
        // plain text — render per baris
        return part.content.split("\n").map((line, j) => (
          line.trim() ? (
            <p key={`${i}-${j}`} className="msg-plain-text">
              {renderMarkdownText(line, "#1f2937")}
            </p>
          ) : null
        ));
      })}
    </div>
  );
}

/* ── Typing Dots ── */
function TypingDots() {
  return (
    <div className="chat-typing">
      <div className="chat-bot-avatar-sm">
        <span>🤖</span>
      </div>
      <div className="chat-typing-bubble">
        <span className="dot" /><span className="dot" /><span className="dot" />
      </div>
    </div>
  );
}

/* ── Message Bubble ── */
function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`msg-row ${isUser ? "user" : "bot"}`}>
      {!isUser && (
        <div className="chat-bot-avatar-sm"><span>🤖</span></div>
      )}
      <div className={`msg-bubble ${isUser ? "user" : "bot"}`}>
        {isUser
          ? <p className="msg-user-text">{msg.content}</p>
          : <MessageContent content={msg.content} />
        }
      </div>
    </div>
  );
}

/* ══════ MAIN CHATBOT PAGE ══════ */
export default function Chatbot() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Halo! Saya **AiDuNode AI** 👋\n\nSaya spesialis **Arduino, ESP32, NodeMCU, dan IoT**. Tanya apa saja tentang kode, rangkaian, atau troubleshooting proyekmu!" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;
    const userMsg = { role: "user", content: trimmed };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    try {
      const response = await api.post("/api/chat", { messages: newMessages });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.reply }]);
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      const errorText = serverMsg || "❌ Terjadi gangguan pada AI. Silakan coba lagi dalam 1 menit.";
      setMessages(prev => [...prev, { role: "assistant", content: errorText }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", content: "Halo! Saya **AiDuNode AI** 👋\n\nSaya spesialis **Arduino, ESP32, NodeMCU, dan IoT**. Tanya apa saja tentang kode, rangkaian, atau troubleshooting proyekmu!" }]);
  };

  const isFirstMessage = messages.length <= 1;

  return (
    <DashboardLayout>
      <div className="chatbot-page">
        {/* ── Header ── */}
        <div className="chatbot-header">
          <div className="chatbot-header-left">
            <div className="chatbot-hdr-avatar">🤖</div>
            <div>
              <h2 className="chatbot-title">AiDuNode AI</h2>
              <div className="chatbot-status">
                <span className="chatbot-dot" /> Online · Spesialis IoT &amp; Arduino
              </div>
            </div>
          </div>
          <button className="chatbot-clear-btn" onClick={clearChat}>🗑️ Bersihkan</button>
        </div>

        {/* ── Messages ── */}
        <div className="chatbot-messages">
          {/* Quick chips hanya muncul saat belum ada percakapan */}
          {isFirstMessage && (
            <div className="chatbot-chips">
              <p className="chatbot-chips-label">💡 Coba tanya:</p>
              <div className="chatbot-chips-row">
                {QUICK_CHIPS.map((c, i) => (
                  <button key={i} className="chatbot-chip" onClick={() => sendMessage(c.label)}>
                    {c.icon} {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} />
          ))}

          {loading && <TypingDots />}
          <div ref={bottomRef} />
        </div>

        {/* ── Input ── */}
        <div className="chatbot-input-area">
          <textarea
            className="chatbot-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Tanya tentang Arduino, ESP32, kode, rangkaian..."
            rows={1}
            disabled={loading}
          />
          <button
            className="chatbot-send-btn"
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
          >
            {loading ? "⏳" : "➤"}
          </button>
        </div>
        <p className="chatbot-hint">Enter untuk kirim · Shift+Enter untuk baris baru</p>
      </div>
    </DashboardLayout>
  );
}
